import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { ThemeContext } from '../contexts/ThemeContext';

const ResetPasswordPage = () => {
  const { darkMode } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get('token');
    const emailParam = params.get('email');
    if (tokenParam && emailParam) {
      setToken(tokenParam);
      setEmail(emailParam);
    } else {
      setError('Invalid password reset link. Token or email is missing.');
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== passwordConfirmation) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    setError('');
    setIsLoading(true);

    try {
      const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
      const response = await fetch(`${baseUrl}/api/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ token, email, password, password_confirmation: passwordConfirmation }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to reset password.');
      }

      setSuccess('Your password has been reset successfully! Redirecting to login...');
      setTimeout(() => navigate('/'), 3000);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const goToLogin = () => {
    navigate('/');
  };

  return (
    <PageContainer>
      <FormContainer>
        <FormWrapper theme={darkMode ? 'dark' : 'light'}>
          <FormHeader>
            <h2>Reset Password</h2>
            <p>Masukkan password baru Anda.</p>
          </FormHeader>
          
          <FormContent>
            {success ? (
              <SuccessMessage>{success}</SuccessMessage>
            ) : (
              <form onSubmit={handleSubmit}>
                <input type="hidden" value={email} readOnly />
                <input type="hidden" value={token} readOnly />
                
                <FormGroup theme={darkMode ? 'dark' : 'light'}>
                  <label htmlFor="password">Password Baru</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Masukan password baru Anda"
                    required
                  />
                  <PasswordRequirement>Minimal 8 karakter.</PasswordRequirement>
                </FormGroup>

                <FormGroup theme={darkMode ? 'dark' : 'light'}>
                  <label htmlFor="password_confirmation">Konfirmasi Password Baru</label>
                  <input
                    type="password"
                    id="password_confirmation"
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    placeholder="Konfirmasi password baru Anda"
                    required
                  />
                </FormGroup>

                {error && <ErrorMessage>{error}</ErrorMessage>}
                
                <FormActions>
                  <BackButton type="button" onClick={goToLogin} disabled={isLoading}>
                    Kembali ke Login
                  </BackButton>
                  <SubmitButton type="submit" disabled={isLoading}>
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                  </SubmitButton>
                </FormActions>
              </form>
            )}
          </FormContent>
        </FormWrapper>
      </FormContainer>
    </PageContainer>
  );
};

// Add your styled-components here, similar to ForgetPasswordPage.js
// For brevity, I'm reusing the names, assuming they are exported or defined in a common file.
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background-color: var(--background);
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const FormWrapper = styled.div`
  background-color: ${props => props.theme === 'dark' ? '#2d2d2d' : 'white'};
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const FormHeader = styled.div`
  padding: 1.5rem;
  text-align: center;
  border-bottom: 1px solid var(--border);
  
  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }
  
  p {
    color: var(--text-light);
    font-size: 0.875rem;
  }
`;

const FormContent = styled.div`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text-dark);
  }
  
  input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-family: inherit;
    font-size: 1rem;
    color: var(--text-dark);
    background-color: ${props => props.theme === 'dark' ? '#383838' : '#f5f5f5'};
    
    &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 2px rgba(43, 93, 161, 0.2);
      outline: none;
    }
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;
`;

const SubmitButton = styled(Button)`
  background-color: var(--primary);
  color: white;
  border: none;
  
  &:hover {
    background-color: var(--primary-light);
  }

  &:disabled {
    background-color: var(--primary-light);
    cursor: not-allowed;
  }
`;

const BackButton = styled(Button)`
  background-color: transparent;
  color: var(--text-dark);
  border: 1px solid var(--border);
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const SuccessMessage = styled.div`
  color: #00a389;
  font-size: 1rem;
  text-align: center;
  padding: 1rem;
  background-color: #e6f8f5;
  border-radius: 8px;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const PasswordRequirement = styled.p`
  color: var(--text-light);
  font-size: 0.75rem;
  margin-top: 0.5rem;
`;

export default ResetPasswordPage; 